import React, { useState, useEffect } from "react";
import { useFonts } from "expo-font";
import { View, ActivityIndicator, StyleSheet } from "react-native";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
} from "react-router-dom";

import * as Sentry from "@sentry/react-native";
import {
  CheckoutReturn,
  ForgotPassword,
  Home,
  HomeAnalytics,
  HomeBeforeAfterV2,
  HomeGetStarted,
  HomePatients,
  HomeMessages,
  HomeConversations,
  HomeSimulate,
  HomeUpgrade,
  HomeGallery,
  HomeConsults,
  HomeLandingPage,
  DuplicateImagesReviewPage,
  HomeAppointments,
  KeyManagementPage,
  ImageToolsPage,
  PublicBillingPage,
  BillingAdminPage,
  AdminAnalyticsPage,
  AdminActivityStreamPage,
  AdminClinicSignupsPage,
  AdminManagementPage,
  NotFound,
  ResetPassword,
  SetupPassword,
  ShareSimulation,
  Signin,
  Signup,
  PatientApp,
  PatientSignup,
  DebugPage,
  BeforeAfterModalDebugPage,
  CorsDebugPage,
  ImageToolsRenderQualityLabPage,
  TemplateManagerPage,
  TemplateNavigationPage,
  PhotoGallery,
  PhotoDeepLinkPage,
} from "./src/components/pages";

import SurgeonReferralWebsite from "./src/components/pages/PalladinoReferral";
import ISAPSOlympiadLanding from "./src/components/pages/SingaporeLanding";
import WAGSConferenceLanding from "./src/components/pages/WagsLanding";
import PipedriveOAuthCallback from "./src/components/pages/home/integrations/pipedrive/pipedriveOAuth";
import DrChronoOAuthCallback from "./src/components/pages/home/integrations/drchrono/drchronoOAuth";
import OAuthInitiatorPage from "./src/components/pages/home/integrations/OAuthInitiator";
import IntegrationsV2Page from "./src/components/pages/home/integrations-v2/IntegrationsV2Page";
import HomeCallSessionBoundary from "./src/components/pages/home/conversations/call-session/HomeCallSessionBoundary";

import { SessionProvider } from "./src/contexts";
import SupabaseNavigation from "./src/navigation/SupabaseNavigation";
import "./src/assets/fonts/fonts.css";
import { OnboardingTipsProvider } from "./src/contexts/OnboardingTipsContext";
import { OnboardingSettingsProvider } from "./src/contexts/OnboardingSettingsContext";
import { mixpanel } from "./src/utils/mixpanel";

const isProductionEnv = process.env.EXPO_PUBLIC_ENV === "production";

if (isProductionEnv)
  Sentry.init({
    dsn: "https://7506e5f02920782657c02b4962883778@o4505506489106432.ingest.us.sentry.io/4507393117126656",
    tracesSampleRate: 1.0,
    _experiments: {
      profilesSampleRate: 1.0,
    },
  });

const router = createBrowserRouter(
  createRoutesFromElements(
    // Root route can be minimal or provide a general layout an <Outlet/> for child routes
    <Route path="/" element={<Outlet />}>
      {/* Public Routes */}
      <Route path="/signin" element={<Signin />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/referral" element={<SurgeonReferralWebsite />} />
      <Route path="/wags" element={<WAGSConferenceLanding />} />
      <Route path="/isaps" element={<ISAPSOlympiadLanding />} />
      <Route
        path="/referral/:referrerCode"
        element={<SurgeonReferralWebsite />}
      />
      <Route path="/return" element={<CheckoutReturn />} />
      <Route path="/patient-app" element={<PatientApp />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/patient-signup" element={<PatientSignup />} />
      <Route path="/simulation/:id" element={<ShareSimulation />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/billing" element={<PublicBillingPage />} />
      <Route path="/billing-admin" element={<BillingAdminPage />} />
      <Route path="/admin-analytics" element={<AdminAnalyticsPage />} />
      <Route
        path="/admin-activity-stream"
        element={<AdminActivityStreamPage />}
      />
      <Route path="/admin-new-users" element={<AdminClinicSignupsPage />} />
      <Route path="/admin-management" element={<AdminManagementPage />} />
      <Route path="/pipedrive-oauth" element={<PipedriveOAuthCallback />} />
      <Route
        path="/image-tools-render-quality-lab"
        element={<ImageToolsRenderQualityLabPage />}
      />

      {/* Authenticated Routes Wrapper */}
      <Route path="/" element={<SupabaseNavigation />}>
        <Route
          path="/"
          element={
            <HomeCallSessionBoundary>
              <Home />
            </HomeCallSessionBoundary>
          }
        >
          {" "}
          {/* Or make Home the index route for authenticated section */}
          <Route index element={<HomeLandingPage />} />{" "}
          {/* Default view for authenticated / */}
          <Route path="upgrade" element={<HomeUpgrade />} />
          <Route path="gallery" element={<HomeGallery />} />
          <Route path="photo-gallery" element={<PhotoGallery />} />
          <Route path="image-tools" element={<ImageToolsPage />} />
          <Route path="photo/:imageId" element={<PhotoDeepLinkPage />} />
          <Route path="patients" element={<HomePatients />} />
          <Route path="leads" element={<HomePatients />} />
          <Route path="simulate" element={<HomeSimulate />} />
          <Route path="messages" element={<HomeMessages />} />
          <Route path="conversations" element={<HomeConversations />} />
          <Route path="calls" element={<HomeConversations />} />
          <Route path="analytics" element={<HomeAnalytics />} />
          <Route path="deploy-widget" element={<HomeGetStarted />} />
          <Route path="before-afters" element={<HomeBeforeAfterV2 />} />
          <Route path="consults" element={<HomeConsults />} />
          <Route path="home" element={<HomeLandingPage />} />{" "}
          <Route path="appointments" element={<HomeAppointments />} />
          <Route path="key-management" element={<KeyManagementPage />} />
          <Route
            path="duplicate-images"
            element={<DuplicateImagesReviewPage />}
          />
          <Route path="integrations" element={<OAuthInitiatorPage />} />
          <Route path="integrations-v2" element={<IntegrationsV2Page />} />
          <Route path="debug" element={<DebugPage />} />
          <Route
            path="before-after-modal-debug"
            element={<BeforeAfterModalDebugPage />}
          />
          <Route path="admin/cors-debug" element={<CorsDebugPage />} />
          <Route
            path="admin-management/cors-debug"
            element={<CorsDebugPage />}
          />
          <Route
            path="note-template-manager"
            element={<TemplateNavigationPage />}
          />
          {/* Dynamic OAuth routes */}
          <Route
            path="/drchrono-top-aesthetics-oauth"
            element={<DrChronoOAuthCallback clinic="top-aesthetics" />}
          />
          <Route
            path="/drchrono-cameo-surgery-oauth"
            element={<DrChronoOAuthCallback clinic="cameo-surgery" />}
          />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  const [fontsLoaded] = useFonts({
    Urbanist100: require("./src/assets/fonts/Urbanist-100.ttf"),
    Urbanist200: require("./src/assets/fonts/Urbanist-200.ttf"),
    Urbanist300: require("./src/assets/fonts/Urbanist-300.ttf"),
    Urbanist400: require("./src/assets/fonts/Urbanist-400.ttf"),
    Urbanist500: require("./src/assets/fonts/Urbanist-500.ttf"),
    Urbanist600: require("./src/assets/fonts/Urbanist-600.ttf"),
    Urbanist700: require("./src/assets/fonts/Urbanist-700.ttf"),
    Urbanist800: require("./src/assets/fonts/Urbanist-800.ttf"),
  });

  // Check for fonts loading
  useEffect(() => {
    if (fontsLoaded) {
      // Wait a bit for supabase to initialize
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fontsLoaded]);

  useEffect(() => {
    let isMounted = true;
    mixpanel.init().catch((error) => {
      if (isMounted) {
        console.error("Mixpanel initialization failed:", error);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // If fonts are still loading or we're in initial loading state, show loading indicator
  if (isLoading || !fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#00B7F4" />
      </View>
    );
  }

  const appBody = (
    <SessionProvider>
      <OnboardingSettingsProvider>
        {/* Hold state about whether to show tips or not */}
        <OnboardingTipsProvider>
          {/* Hold state about the tips themselves */}
          <RouterProvider router={router} />
        </OnboardingTipsProvider>
      </OnboardingSettingsProvider>
    </SessionProvider>
  );

  return isProductionEnv ? (
    appBody
  ) : (
    <React.StrictMode>{appBody}</React.StrictMode>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
});

export default isProductionEnv ? Sentry.wrap(App) : App;
